import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { PAGES, pageUrl, seoText } from "../../seo";
import { useT, useLang } from "../../i18n";
import { num } from "../../api";
import { useShop, PhoneCard, Photo, Calc, CallButton, Socials } from "../../component/shop";

// «Телефоны в <em>Нукусе</em>» → выделенное слово без innerHTML
const Accent = ({ text }) => text.split(/<em>|<\/em>/).map((part, i) => (i % 2 ? <em key={i}>{part}</em> : part));

// Магазин на карте (Дихан базар, магазин №9)
const MAP_LAT = 42.46180678692264, MAP_LNG = 59.6039235670718;
const MAP_EMBED = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d794.4971244238023!2d59.6039235670718!3d42.46180678692264!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x41dd9b2d17d94c03%3A0x488186e92353456!2shoco!5e1!3m2!1sru!2s!4v1761855281978!5m2!1sru!2s";

const Arrow = () => <svg viewBox="0 0 24 24"><path d="M5 12h14M13 6l6 6-6 6" /></svg>;

// «Ценник» в первом экране: листает лучшие телефоны из наличия
const HeroTag = ({ phones }) => {
    const t = useT();
    const shop = useShop();
    const top = useMemo(() => [...phones].filter((p) => (p.battery ?? 100) >= 95).sort((a, b) => b.price - a.price).slice(0, 4), [phones]);
    const [i, setI] = useState(0);
    const [auto, setAuto] = useState(true);
    useEffect(() => {
        if (!auto || top.length < 2 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const tm = setInterval(() => setI((x) => x + 1), 4500);
        return () => clearInterval(tm);
    }, [auto, top.length]);
    if (!top.length) return null;
    const p = top[i % top.length];
    const money = (n) => num(n) + " " + t("sum");
    return (
        <div className="tag">
            <div className="tag-top"><span>{t("tag_top")}</span>{p.battery != null && <span>{p.battery}% {t("bat").toLowerCase()}</span>}</div>
            <div className="tag-img"><Photo phone={p} /></div>
            <div><h3>{p.model}</h3><p className="spec">{p.memory} · {p.color}</p></div>
            <div className="row">
                <div><div className="price num">{money(p.price)}</div><div className="mo">{(t("from") ? t("from") + " " : "") + money(p.fromMonthly) + t("per_mo")}</div></div>
                <button className="btn sm" onClick={() => shop.open(p.id)}>{t("calc_monthly")}</button>
            </div>
            {top.length > 1 && <div className="tag-dots">{top.map((_, k) => <button key={k} className={k === i % top.length ? "on" : ""} onClick={() => { setI(k); setAuto(false); }} aria-label={String(k + 1)} />)}</div>}
        </div>
    );
};

const Home = ({ pageKey = "home" }) => {
    const t = useT();
    const { lang } = useLang();
    const shop = useShop();
    const location = useLocation();
    const phones = shop.phones;
    const sec = PAGES[pageKey]?.sec; // /rassrochka, /kontakty — прокрутить к блоку

    useEffect(() => { if (!sec) window.scrollTo(0, 0); }, [sec, location.key]);
    useEffect(() => {
        if (sec && phones) requestAnimationFrame(() => document.getElementById(sec)?.scrollIntoView());
    }, [sec, phones, location.key]);

    // Вперемешку: по очереди из каждой марки, сначала новые
    const mixed = useMemo(() => {
        if (!phones) return [];
        const by = {};
        phones.forEach((p) => (by[p.brand] ||= []).push(p));
        const qs = Object.values(by);
        const out = [];
        for (let i = 0; out.length < 8 && i < 50; i++) qs.forEach((q) => q[i] && out.length < 8 && out.push(q[i]));
        return out;
    }, [phones]);
    const names = useMemo(() => [...new Set((phones || []).map((p) => p.model))], [phones]);
    const calcPhone = useMemo(() => (phones?.length ? [...phones].sort((a, b) => b.price - a.price)[Math.floor(phones.length / 3)] : null), [phones]);

    return (
        <main>
            <section className="hero">
                <div className="wrap">
                    <div>
                        <span className="eyebrow"><i></i><span>{phones ? t("hero_eyebrow", { n: phones.length }) : t("loading")}</span></span>
                        <h1>{pageKey === "home" ? <Accent text={t("hero_h1")} /> : seoText(lang, pageKey).h1}</h1>
                        <p className="lead">{t("hero_lead")}</p>
                        <div className="ctas">
                            <Link className="btn" to={pageUrl(lang, "catalog")}>{t("cta_catalog")}<Arrow /></Link>
                            <CallButton className="btn ghost" />
                        </div>
                        <div className="facts">
                            <div><b className="num">{phones ? phones.length : "…"}</b><span>{t("f_stock")}</span></div>
                            <div><b>{t("f_credit_v")}</b><span>{t("f_credit")}</span></div>
                            <div><b>{t("f_check_v")}</b><span>{t("f_check")}</span></div>
                        </div>
                    </div>
                    {phones && <HeroTag phones={phones} />}
                </div>
            </section>
            {names.length > 0 && <div className="strip" aria-hidden="true"><div className="strip-in">{[...names, ...names].map((n, k) => <span key={k}>{n}</span>)}</div></div>}

            <section className="sec">
                <div className="wrap">
                    <div className="sec-h"><div><h2>{t("svc_h")}</h2><p>{t("svc_p")}</p></div></div>
                    <div className="svc">
                        <article><span className="ic"><svg viewBox="0 0 24 24"><path d="M12 2v20M17 6H9.5a3 3 0 0 0 0 6h5a3 3 0 0 1 0 6H6" /></svg></span><h3>{t("s1_h")}</h3><p>{t("s1_p")}</p><CallButton className="btn ghost sm">{t("s1_b")}</CallButton></article>
                        <article><span className="ic"><svg viewBox="0 0 24 24"><path d="M8 2h8a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zM11 18h2" /></svg></span><h3>{t("s2_h")}</h3><p>{t("s2_p")}</p><Link className="btn light sm" to={pageUrl(lang, "catalog")}>{t("s2_b")}</Link></article>
                        <article><span className="ic"><svg viewBox="0 0 24 24"><path d="M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM21 21l-5-5M11 8v6M8 11h6" /></svg></span><h3>{t("s3_h")}</h3><p>{t("s3_p")}</p><CallButton className="btn ghost sm">{t("s3_b")}</CallButton></article>
                    </div>
                </div>
            </section>

            <section className="sec" style={{ paddingTop: 0 }}>
                <div className="wrap">
                    <div className="sec-h"><div><h2>{t("stock_h")}</h2><p>{t("stock_p")}</p></div><Link className="btn ghost" to={pageUrl(lang, "catalog")}>{t("cta_all")}<Arrow /></Link></div>
                    {shop.error ? <div className="empty"><b>{t("load_err")}</b></div>
                        : !phones ? <div className="empty">{t("loading")}</div>
                        : !phones.length ? <div className="empty"><b>{t("no_stock_h")}</b>{t("no_stock_p")}</div>
                        : <div className="grid">{mixed.map((p) => <PhoneCard key={p.id} p={p} />)}</div>}
                </div>
            </section>

            <section className="sec" id="credit" style={{ paddingTop: 0 }}>
                <div className="wrap">
                    <div className="credit">
                        <div>
                            <h2>{t("cr_h")}</h2>
                            <p className="sub">{t("cr_p")}</p>
                            <ol className="steps"><li>{t("cr_s1")}</li><li>{t("cr_s2", { p: shop.info?.minAdvancePct ?? 30 })}</li><li>{t("cr_s3")}</li><li>{t("cr_s4")}</li></ol>
                        </div>
                        <div className="calc">{calcPhone ? <Calc phone={calcPhone} selectable /> : <p className="fine">{t("loading")}</p>}</div>
                    </div>
                </div>
            </section>

            <section className="sec" id="contacts" style={{ paddingTop: 0 }}>
                <div className="wrap">
                    <div className="sec-h"><div><h2>{t("ct_h")}</h2><p>{t("ct_p")}</p></div></div>
                    <div className="contacts">
                        <div className="cinfo">
                            <div className="crow"><span className="ic"><svg viewBox="0 0 24 24"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" /></svg></span><div><small>{t("ct_phone")}</small><b>+998 77 116 70 00</b></div></div>
                            <div className="crow"><span className="ic"><svg viewBox="0 0 24 24"><path d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12zM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" /></svg></span><div><small>{t("ct_addr")}</small><b>{t("addr")}</b></div></div>
                            <div className="crow"><span className="ic"><svg viewBox="0 0 24 24"><path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 7v5l3 2" /></svg></span><div><small>{t("ct_hours")}</small><b>{t("hours")}</b></div></div>
                            <div className="crow"><span className="ic"><svg viewBox="0 0 24 24"><path d="M4 12a8 8 0 1 0 16 0 8 8 0 0 0-16 0zM4 12h16M12 4c2.5 2.2 2.5 13.8 0 16M12 4c-2.5 2.2-2.5 13.8 0 16" /></svg></span><div><small>{t("ct_social")}</small><Socials /></div></div>
                            <CallButton />
                        </div>
                        <div className="map-real">
                            <iframe title={t("ct_h")} src={MAP_EMBED} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen></iframe>
                            <div className="map-links">
                                <a className="btn ghost sm" href={`https://www.google.com/maps/search/?api=1&query=${MAP_LAT},${MAP_LNG}`} target="_blank" rel="noopener noreferrer">{t("ct_gmaps")}</a>
                                <a className="btn ghost sm" href={`https://yandex.uz/maps/?pt=${MAP_LNG},${MAP_LAT}&z=17&l=map`} target="_blank" rel="noopener noreferrer">{t("ct_ymaps")}</a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Home;
